import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { Modal } from 'react-bootstrap';
import { Formik } from 'formik';
import {
  FiAlertTriangle,
  FiBookOpen,
  FiEdit2,
  FiInfo,
  FiPlus,
  FiTrash2,
} from 'react-icons/fi';

import BasePage from '../../../../components/BasePage';
import DataTable, { Column } from '../../../../components/DataTable';
import { useToast } from '../../../../hooks/useToast';
import api from '../../../../services/api';

import { CreateButton, DefaultBadge, IconButton, PostCount } from './styles';
import {
  DangerAlert,
  FieldBlock,
  FieldHint,
  FooterButton,
  FooterHint,
  Label,
  ModalSubtitle,
  Section,
  SectionHead,
  Select,
  TextArea,
  TextInput,
} from './modalStyles';

interface IPosts {
  title: string;
}

interface ICategory {
  id: string;
  title: string;
  slug: string;
  parent_id: string;
  description: string;
  posts: IPosts[];
  childs: ICategory[];
}

const NewsCategories: React.FC = () => {
  const { addToast } = useToast();

  const [categories, setCategories] = useState<ICategory[]>([]);
  const [loading, setLoading] = useState(true);

  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const [selectedId, setSelectedId] = useState<string>('');
  const [editCategory, setEditCategory] = useState<ICategory | undefined>();

  useEffect(() => {
    document.title = 'Categorias - Viva Voz - Rede Suco de Laranja';
  }, []);

  const loadCategories = useCallback(async () => {
    setLoading(true);
    const response = await api.get<ICategory[]>('/news/categories');
    setCategories(response.data);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const handleOpenEdit = useCallback(async (id: string) => {
    const response = await api.get<ICategory>(`/news/categories/${id}`);
    setSelectedId(id);
    setEditCategory(response.data);
    setShowEdit(true);
  }, []);

  const handleOpenDelete = useCallback((id: string) => {
    setSelectedId(id);
    setShowDelete(true);
  }, []);

  const handleCreateCategory = useCallback(
    async (values, { resetForm }) => {
      try {
        await api.post('/news/categories', values);
        await loadCategories();
        addToast({
          title: 'Sucesso',
          body: 'Categoria criada com sucesso.',
        });
        resetForm();
        setShowAdd(false);
      } catch (error) {
        addToast({
          title: 'Erro',
          body: 'Ocorreu um erro ao tentar adicionar uma nova categoria. Tente novamente.',
        });
      }
    },
    [addToast, loadCategories],
  );

  const handleUpdateCategory = useCallback(
    async ({ title, parent_id, description }) => {
      try {
        await api.patch(`/news/categories/${selectedId}`, {
          title,
          parent_id: parent_id || null,
          description,
        });
        await loadCategories();
        addToast({
          title: 'Sucesso',
          body: 'Categoria atualizada com sucesso.',
        });
        setShowEdit(false);
      } catch (error) {
        addToast({
          title: 'Erro',
          body: 'Ocorreu um erro ao tentar atualizar a categoria. Tente novamente.',
        });
      }
    },
    [addToast, loadCategories, selectedId],
  );

  const handleDeleteCategory = useCallback(async () => {
    try {
      await api.delete(`/news/categories/${selectedId}`);
      await loadCategories();
      addToast({
        title: 'Sucesso',
        body: 'Categoria removida com sucesso.',
      });
      setShowDelete(false);
    } catch (error) {
      addToast({
        title: 'Erro',
        body: 'Ocorreu um erro ao tentar remover a categoria. Tente novamente.',
      });
    }
  }, [addToast, loadCategories, selectedId]);

  const parentOptions = useMemo(
    () =>
      categories
        .filter(c => c.id !== selectedId)
        .map(c => ({
          value: c.id,
          label: c.title,
        })),
    [categories, selectedId],
  );

  const columns = useMemo<Column<ICategory>[]>(
    () => [
      {
        key: 'title',
        label: 'Nome',
        width: '28%',
        render: row => (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              minWidth: 0,
            }}
          >
            <span
              style={{
                fontWeight: 700,
                color: '#0f172a',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {row.title}
            </span>
            {row.slug === 'categoria-padrao' && (
              <DefaultBadge>Padrão</DefaultBadge>
            )}
          </div>
        ),
      },
      {
        key: 'description',
        label: 'Descrição',
        render: row =>
          row.description ? (
            <span
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                color: '#475569',
              }}
              title={row.description}
            >
              {row.description}
            </span>
          ) : (
            <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>
              Sem descrição
            </span>
          ),
      },
      {
        key: 'posts',
        label: 'Posts',
        width: '12%',
        align: 'center',
        render: row => <PostCount>{row.posts.length}</PostCount>,
      },
    ],
    [],
  );

  return (
    <>
      <BasePage title="Categorias">
        <DataTable<ICategory>
          title="Categorias de notícias"
          subtitle="Organize notícias por categorias hierárquicas para facilitar a navegação"
          columns={columns}
          data={categories}
          rowKey="id"
          loading={loading}
          page={1}
          pageSize={categories.length || 1}
          total={categories.length}
          onPageChange={() => null}
          headerActions={(
            <CreateButton type="button" onClick={() => setShowAdd(true)}>
              <FiPlus size={14} />
              Nova categoria
            </CreateButton>
          )}
          rowActions={row =>
            row.slug === 'categoria-padrao' ? (
              <span
                style={{
                  fontSize: 11.5,
                  color: '#94a3b8',
                  fontStyle: 'italic',
                }}
              >
                Não editável
              </span>
            ) : (
              <>
                <IconButton
                  type="button"
                  title="Editar"
                  onClick={() => handleOpenEdit(row.id)}
                >
                  <FiEdit2 size={14} />
                </IconButton>
                <IconButton
                  type="button"
                  title="Remover"
                  $danger
                  onClick={() => handleOpenDelete(row.id)}
                >
                  <FiTrash2 size={14} />
                </IconButton>
              </>
            )}
          emptyMessage="Nenhuma categoria cadastrada."
        />
      </BasePage>

      {/* Add Category Modal */}
      <Modal
        show={showAdd}
        onHide={() => setShowAdd(false)}
        aria-labelledby="contained-modal-title-vcenter"
        centered
        size="lg"
      >
        <Formik
          initialValues={{ title: '', parent_id: '', description: '' }}
          onSubmit={handleCreateCategory}
        >
          {({ values, handleSubmit, handleChange }) => (
            <form onSubmit={handleSubmit}>
              <Modal.Header closeButton>
                <div>
                  <Modal.Title style={{ fontSize: 18, fontWeight: 700 }}>
                    Nova categoria
                  </Modal.Title>
                  <ModalSubtitle>
                    Crie uma categoria para organizar as notícias. Você pode
                    aninhá-la sob uma categoria já existente.
                  </ModalSubtitle>
                </div>
              </Modal.Header>

              <Modal.Body style={{ background: '#fafbfc', padding: 20 }}>
                <Section>
                  <SectionHead>
                    <span className="badge">
                      <FiBookOpen size={16} />
                    </span>
                    <div>
                      <h4>Dados da categoria</h4>
                      <span className="subtitle">
                        Nome, hierarquia e descrição
                      </span>
                    </div>
                  </SectionHead>

                  <FieldBlock>
                    <Label>Nome *</Label>
                    <TextInput
                      type="text"
                      placeholder="Ex.: Saúde e bem-estar"
                      value={values.title}
                      onChange={handleChange('title')}
                    />
                    <FieldHint>
                      Nome que aparecerá no sistema e nas notícias.
                    </FieldHint>
                  </FieldBlock>

                  <FieldBlock>
                    <Label>Categoria ascendente</Label>
                    <Select
                      value={values.parent_id}
                      onChange={handleChange('parent_id')}
                    >
                      <option value="">Sem categoria ascendente</option>
                      {parentOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Select>
                    <FieldHint>
                      Aninhe esta categoria sob outra para criar uma hierarquia.
                    </FieldHint>
                  </FieldBlock>

                  <FieldBlock>
                    <Label>Descrição</Label>
                    <TextArea
                      rows={3}
                      placeholder="Breve descrição do que esta categoria representa"
                      value={values.description}
                      onChange={handleChange('description')}
                    />
                    <FieldHint>
                      Ajuda editores a entenderem quando usar esta categoria.
                    </FieldHint>
                  </FieldBlock>
                </Section>

                <FooterHint>
                  <FiInfo size={13} />
                  <span>
                    Campos com * são obrigatórios. Você pode editar a categoria
                    a qualquer momento.
                  </span>
                </FooterHint>
              </Modal.Body>

              <Modal.Footer>
                <FooterButton
                  type="button"
                  $variant="secondary"
                  onClick={() => setShowAdd(false)}
                >
                  Cancelar
                </FooterButton>
                <FooterButton type="submit" disabled={!values.title.trim()}>
                  Salvar categoria
                </FooterButton>
              </Modal.Footer>
            </form>
          )}
        </Formik>
      </Modal>

      {/* Edit Category Modal */}
      <Modal
        show={showEdit}
        onHide={() => setShowEdit(false)}
        aria-labelledby="contained-modal-title-vcenter"
        centered
        size="lg"
      >
        {editCategory && (
          <Formik
            enableReinitialize
            initialValues={{
              title: editCategory.title || '',
              parent_id: editCategory.parent_id || '',
              description: editCategory.description || '',
            }}
            onSubmit={handleUpdateCategory}
          >
            {({ values, handleSubmit, handleChange }) => (
              <form onSubmit={handleSubmit}>
                <Modal.Header closeButton>
                  <div>
                    <Modal.Title style={{ fontSize: 18, fontWeight: 700 }}>
                      Editar categoria
                    </Modal.Title>
                    <ModalSubtitle>
                      Atualize o nome, a hierarquia ou a descrição desta
                      categoria.
                    </ModalSubtitle>
                  </div>
                </Modal.Header>

                <Modal.Body style={{ background: '#fafbfc', padding: 20 }}>
                  <Section>
                    <SectionHead>
                      <span className="badge">
                        <FiBookOpen size={16} />
                      </span>
                      <div>
                        <h4>Dados da categoria</h4>
                        <span className="subtitle">
                          Nome, hierarquia e descrição
                        </span>
                      </div>
                    </SectionHead>

                    <FieldBlock>
                      <Label>Nome *</Label>
                      <TextInput
                        type="text"
                        placeholder="Ex.: Saúde e bem-estar"
                        value={values.title}
                        onChange={handleChange('title')}
                      />
                      <FieldHint>
                        Nome que aparecerá no sistema e nas notícias.
                      </FieldHint>
                    </FieldBlock>

                    <FieldBlock>
                      <Label>Categoria ascendente</Label>
                      <Select
                        value={values.parent_id}
                        onChange={handleChange('parent_id')}
                      >
                        <option value="">Sem categoria ascendente</option>
                        {parentOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Select>
                      <FieldHint>
                        Aninhe esta categoria sob outra para criar uma
                        hierarquia.
                      </FieldHint>
                    </FieldBlock>

                    <FieldBlock>
                      <Label>Descrição</Label>
                      <TextArea
                        rows={3}
                        placeholder="Breve descrição do que esta categoria representa"
                        value={values.description}
                        onChange={handleChange('description')}
                      />
                      <FieldHint>
                        Ajuda editores a entenderem quando usar esta categoria.
                      </FieldHint>
                    </FieldBlock>
                  </Section>
                </Modal.Body>

                <Modal.Footer>
                  <FooterButton
                    type="button"
                    $variant="secondary"
                    onClick={() => setShowEdit(false)}
                  >
                    Cancelar
                  </FooterButton>
                  <FooterButton type="submit" disabled={!values.title.trim()}>
                    Salvar alterações
                  </FooterButton>
                </Modal.Footer>
              </form>
            )}
          </Formik>
        )}
      </Modal>

      {/* Delete Category Modal */}
      <Modal
        show={showDelete}
        onHide={() => setShowDelete(false)}
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton>
          <div>
            <Modal.Title style={{ fontSize: 18, fontWeight: 700 }}>
              Remover categoria
            </Modal.Title>
            <ModalSubtitle>
              Confirme a remoção desta categoria do sistema.
            </ModalSubtitle>
          </div>
        </Modal.Header>
        <Modal.Body style={{ padding: 20 }}>
          <DangerAlert>
            <FiAlertTriangle size={18} />
            <span>
              Você realmente deseja remover esta categoria? Essa ação não poderá
              ser desfeita. As notícias vinculadas serão movidas para a
              categoria padrão.
            </span>
          </DangerAlert>
        </Modal.Body>
        <Modal.Footer>
          <FooterButton
            type="button"
            $variant="secondary"
            onClick={() => setShowDelete(false)}
          >
            Cancelar
          </FooterButton>
          <FooterButton
            type="button"
            $variant="danger"
            onClick={handleDeleteCategory}
          >
            Remover categoria
          </FooterButton>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default NewsCategories;
